import { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { setDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { mapFirebaseError } from '../functions/classes';
import { Navbar2 } from '../functions/ui';
import { PiEye, PiEyeSlash } from 'react-icons/pi';
import '../webapp.css';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rePassword, setRePassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [rePasswordError, setRePasswordError] = useState('');
  const [firebaseError, setFirebaseError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validations, setValidations] = useState({
    capitalLetter: false,
    number: false,
    specialCharacter: false,
    minLength: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showRePassword, setShowRePassword] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);

  const navigate = useNavigate();

  const validateInputs = () => {
    let isValid = true;

    // Email validation
    if (!email) { //1
      setEmailError('Email is required');
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) { //2
        setEmailError('Please enter a valid email');
        isValid = false;
      } else {
        setEmailError('');
      }
    }

    // Password validation
    if (!validations.capitalLetter || !validations.number || !validations.specialCharacter || !validations.minLength) {
      setPasswordError('Password does not meet all criteria');
      isValid = false;
    } else {
      setPasswordError('');
    }

    // Re-password validation
    if (!rePassword) {
      setRePasswordError('Please confirm your password');
      isValid = false;
    } else if (rePassword !== password) {
      setRePasswordError('Passwords do not match');
      isValid = false;
    } else {
      setRePasswordError('');
    }

    return isValid;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Generate company code
      const companycode = generateCompanyCode();

      // Create a document in 'companyUsers' collection with the UID
      const userDocRef = doc(db, 'companyUsers', user.uid);
      await setDoc(userDocRef, {
        email: user.email,
        companycode,
        createdAt: new Date(),
        premium: false,
        feedback: false,
        onboarded: false // Mark user as not onboarded at signup
      });

      // Save company code to 'shared' collection
      const suggestionDocRef = doc(db, 'shared', user.uid);
      await setDoc(suggestionDocRef, {
        companycode,
      });

      // Navigate to the onboarding page after successful signup
      // Track Google Ads signup conversion (fires once on successful signup)
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17728169914/qWFGCNvqyb8bELrPuYVC'
        });
      }
      navigate('/onboard');
    } catch (error) {
      if (error.code) {
        setFirebaseError(mapFirebaseError(error.code));
      } else {
        setFirebaseError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateCompanyCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$&*()_+-<>?';
    let code = '';
    for (let i = 0; i < 4; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  const validateRegex = (pattern, text) => {
    return new RegExp(pattern).test(text);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    // Use the correct regex patterns for live validation
    const capitalLetterRegEx = '.*[A-Z]+.*';
    const numberRegEx = '.*[0-9]+.*';
    const specialCharacterRegEx = '.*[!@#$%^&*(),.?":{}|<>]+.*';
    setValidations({
      capitalLetter: validateRegex(capitalLetterRegEx, value),
      number: validateRegex(numberRegEx, value),
      specialCharacter: validateRegex(specialCharacterRegEx, value),
      minLength: value.length >= 6,
    });
  };

  return (
    <>
      <Navbar2 />
      <div className="getaccess-wrapper">
        <div className="getaccess-container">
          <h2>Sign up</h2>
          <form onSubmit={handleSignUp}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="text"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailError('')}
              />
              {emailError && <p className="error-message">{emailError}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>  
              <div className='passwordbox'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  onFocus={() => {
                    setPasswordError('');
                    setShowPasswordCriteria(true);
                  }}
                  onBlur={() => setShowPasswordCriteria(false)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="showhide"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <PiEyeSlash/> : <PiEye/>}
                </button>
              </div>
              {passwordError && <p className="error-message">{passwordError}</p>}
              {showPasswordCriteria && (
                <div className="form-group">
                  <p className={validations.capitalLetter ? 'success-message' : 'error-message'}>
                    Contains a capital letter (A-Z)
                  </p>
                  <p className={validations.number ? 'success-message' : 'error-message'}>
                    Contains a number (0-9)
                  </p>
                  <p className={validations.specialCharacter ? 'success-message' : 'error-message'}>
                    Contains a special character (e.g. @, #, $)
                  </p>
                  <p className={validations.minLength ? 'success-message' : 'error-message'}>
                    At least 6 characters
                  </p>
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="rePassword">Confirm Password</label>
              <div className='passwordbox'>
                <input
                  type={showRePassword ? 'text' : 'password'}
                  id="rePassword"
                  value={rePassword}
                  onChange={(e) => setRePassword(e.target.value)}
                  onFocus={() => {
                    setRePasswordError('');
                    setFirebaseError('');
                  }}
                  style={{ paddingRight: '36px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowRePassword((prev) => !prev)}
                  className="showhide"
                  tabIndex={-1}
                  aria-label={showRePassword ? 'Hide password' : 'Show password'}
                >
                  {showRePassword ? <PiEyeSlash/> : <PiEye/>}
                </button>
              </div>
              {rePasswordError && <p className="error-message">{rePasswordError}</p>}
              {firebaseError && <p className="error-message">{firebaseError}</p>}
            </div>
            <button className='full-button' disabled={loading}>
              {loading ? 'Signing up...' : 'Sign up'}
            </button>
          </form>
          <div className="login-link">
            Already have an account? <a href="/login">Log in</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;

//Done